import { useState, useEffect, useCallback, useRef } from 'react';
import {
  TableContainer,
  TablePagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  IconButton,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import AddProductionModal from './AddProductionModal';
import ProductionDetailModal from './ProductionDetailModal';
import { getBatches } from '../../services/ProductionService';
import { getUnitById } from '../../utils/Enums';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';

function BatchTable({ locationId }) {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({ add: false, view: false });
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const fetchBatches = useCallback(async (pageNum = 1, pageSize = 10, search = '') => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const data = await getBatches(locationId, pageNum, pageSize, cleanSearch);

      if (data && typeof data === 'object') {
        setBatches(data.productions || data.batches || data.data || []);
        setTotalRows(data.total || data.totalCount || 0);
      } else {
        setBatches(data || []);
        setTotalRows(data?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      setBatches([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchBatches(page + 1, rowsPerPage, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchBatches]);

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPage(0);
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchBatches(1, rowsPerPage, searchTerm);
    }, 500);
  };

  const handleModal = (type, open, batch = null) => {
    setModalState(prev => ({ ...prev, [type]: open }));
    setSelectedBatch(batch);
  };

  const handleAddBatch = async (newBatch) => {
    // Refetch data after adding
    fetchBatches(page + 1, rowsPerPage, searchTerm);
    handleModal('add', false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchBatches(newPage + 1, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchBatches(1, newRowsPerPage, searchTerm);
  };

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_production_name')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: '300px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton
              loading={loading}
              onClick={() => fetchBatches(page + 1, rowsPerPage, searchTerm)}
            />
            <Button
              variant="outlined"
              startIcon={getIcon('add')}
              onClick={() => handleModal('add', true)}
            >
              {t('add_production')}
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table
            stickyHeader
            size="small"
            aria-label="batch table"
            sx={{
              '& .MuiTableCell-root': {
                py: 1.25,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                py: 1.5,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('type')}</TableCell>
                <TableCell align="center">{t('product')}</TableCell>
                <TableCell align="center">{t('size')}</TableCell>
                <TableCell align="center">{t('production_date')}</TableCell>
                <TableCell align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                  <TableRow>
                  <TableCell colSpan={6} align="center">
                      <Box py={1}  justifyContent="center">
                      <CircularProgress size={32} />
                      </Box>
                  </TableCell>
                  </TableRow>
              ) : (
                batches.map(batch => (
                  <TableRow key={batch.id}>
                    <TableCell>{batch.processType === 'elaboration' ? t('elaboration') : t('packing')}</TableCell>
                    <TableCell align="center">{batch.name}</TableCell>
                    <TableCell align="center">{
                      batch.batchSizeActual > 0
                      ? `${batch.batchSizeActual.toLocaleString('de-DE')} ${getUnitById(batch?.batchUnit).label}`
                      : '–'}
                    </TableCell>                    
                    <TableCell align="center">
                      {new Date(batch.productionDate).toLocaleDateString('es-UY', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleModal('view', true, batch)}>
                        {getIcon('eye')}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {modalState.add && (
        <AddProductionModal
          locationId={locationId}
          open={modalState.add}
          onSave={handleAddBatch}
          onClose={() => handleModal('add', false)}
        />
      )}

      {modalState.view && selectedBatch?.id && (
        <ProductionDetailModal
          locationId={locationId}
          open={modalState.view}
          productionId={selectedBatch.id}
          onClose={() => handleModal('view', false)}
        />
      )}

    </>
  );
}

export default BatchTable;
